/**
 * useTutorActivityQuizReview — dans la vue « Quizz » de la fiche d'une activité, l'avis du tuteur sur le
 * quiz de prérequis et « Signaler » une question (`TodoModal.js:646-671`, `QuizQuestionSignalButton`).
 *
 *  - **L'avis** : prérempli de `prerequisite_quiz_attempt.tutor_feedback`, donné **une seule fois** ;
 *    mesure `checkpoint.feedback.button_click` (le pouce, `FeedbackButton`) et `quiz.tutor.feedback.submit`
 *    (`quiz_id`, `activity_id`, `feedback_value`), puis
 *    `giveQuizFeedback({ activityId }, { feedback_value: 1 | -1 })`.
 *  - **« Signaler »** (`context: "review"`) : la machine du moteur (`quizSignalReducer`), mesures
 *    `quiz.question.signal.button_click` et `…submitted`, puis `signalQuizQuestion({ activityId },
 *    { quiz_question_id, signal_type, context, comment })`, et l'accusé de l'ancien.
 *
 * Le même geste que le centre de la salle du live (`Room/useTutorLiveCenterReviews.ts`), qui y joint
 * l'avis sur la correction ; la fiche hors live mesure déjà ce dernier à sa façon (`activity.correction_review`).
 * Les deux crochets se fondront quand la salle se rouvrira à l'édition : ses lectures pures sont déjà
 * partagées (`liveQuizTutorFeedback`, `quizFeedbackOf`, `quizQuestionIdAt`).
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useCallback, useEffect, useReducer, useRef, useState } from "react"
import { useDispatch } from "react-redux"

import { activitiesService } from "../../../../../../data/services/activitiesService"
import {
    INITIAL_QUIZ_SIGNAL,
    quizSignalReducer,
    type QuizSignalType,
} from "../../../../../../engine/activity/prerequisite/quizQuestionSignal"
import { apiCall, apiFailureOf, type ApiService } from "../../../../../../engine/shared/apiCall"
import { type QuizQuestionSignalSheetProps } from "../../../../../../design-system/organisms/QuizQuestionSignalSheet/QuizQuestionSignalSheet"
import { liveQuizTutorFeedback, type ApiLiveActivity } from "../../../../adapters/tutor/Live/tutorLiveActivityAdapter"
import { quizFeedbackOf, quizQuestionIdAt } from "../../../../view-models/tutor/Live/tutorLiveCenterViewModel"
import { apiErrorMessage } from "../../../hooks/apiErrorMessage"
import { type UICorrectionFeedback } from "../../../types/studentActivity"
import { type GestureName } from "../../../hooks/gestureTracking"

const SIGNAL_CONTEXT = "review"

/** Les mots de l'ancien (`QuizQuestionSignalButton`), recopiés pour ne pas tirer la salle dans Mes élèves. */
export const QUIZ_REVIEW_TEXTS = {
    signalSent: "Merci pour votre réponse. Pour ne pas rester bloqué, merci de tout de même choisir une réponse",
    feedbackFailed: "Ton avis n'est pas parti. Réessaie dans un instant.",
}

type Notify = (message: string, tone: "success" | "danger") => void

/** La mesure de la fiche (`gesture.*`) : une écriture mesurée, et l'ouverture de sa feuille. */
export type QuizReviewMeasure = {
    measured: <T>(gesture: GestureName, promise: () => Promise<T>) => Promise<T>
    opened: (gesture: GestureName) => void
}

export type TutorActivityQuizReview = {
    feedback: { value: UICorrectionFeedback | null; onChange: (value: UICorrectionFeedback) => void } | null
    onSignalQuestion: (index: number) => void
    signal: QuizQuestionSignalSheetProps | null
}

export function useTutorActivityQuizReview(
    raw: ApiLiveActivity | null,
    notify: Notify,
    measure: QuizReviewMeasure
): TutorActivityQuizReview {
    const dispatch = useDispatch() as unknown as (action: never) => unknown
    const call = useCallback(
        <T = unknown>(service: unknown, params: Parameters<typeof apiCall>[2] = {}) =>
            apiCall<T>(dispatch, service as ApiService, params),
        [dispatch]
    )
    const activityId = raw?.id ?? null
    const notifyRef = useRef(notify)
    notifyRef.current = notify
    const { measured, opened } = measure

    // ── L'avis sur le quiz ──
    const served = liveQuizTutorFeedback(raw)
    const [value, setValue] = useState<1 | -1 | null>(served)
    useEffect(() => {
        setValue(served)
    }, [served, activityId])

    const onFeedback = useCallback(
        (feedback: UICorrectionFeedback) => {
            // Une seule fois : l'avis donné ne se change plus (`handleQuizFeedback`).
            if (value !== null || activityId == null) return
            const given = feedback === "positive" ? 1 : -1
            // Le pouce de l'ancienne vue (`FeedbackButton`) mesure son toucher, puis l'avis.
            amplitude.track("checkpoint.feedback.button_click")
            amplitude.track("quiz.tutor.feedback.submit", {
                quiz_id: raw?.prerequisite_quiz?.id,
                activity_id: activityId,
                feedback_value: given,
            })
            setValue(given)
            measured("activity.quiz_feedback", () =>
                call<{ tutor_feedback?: number | null }>(activitiesService.giveQuizFeedback, {
                    routeParams: { activityId },
                    data: { feedback_value: given },
                })
            )
                .then((answer) => {
                    const served = answer?.tutor_feedback
                    if (served === 1 || served === -1) setValue(served)
                })
                .catch((cause) =>
                    notifyRef.current(apiErrorMessage(apiFailureOf(cause), QUIZ_REVIEW_TEXTS.feedbackFailed), "danger")
                )
        },
        [activityId, call, measured, raw?.prerequisite_quiz?.id, value]
    )

    // ── « Signaler » une question ──
    const [signal, dispatchSignal] = useReducer(quizSignalReducer, INITIAL_QUIZ_SIGNAL)
    const [questionId, setQuestionId] = useState<number | null>(null)

    const onSignalQuestion = useCallback(
        (index: number) => {
            const id = quizQuestionIdAt(raw, index)
            amplitude.track("quiz.question.signal.button_click", { context: SIGNAL_CONTEXT, question_id: id })
            setQuestionId(id)
            dispatchSignal({ type: "opened" })
            opened("activity.quiz_signal")
        },
        [opened, raw]
    )

    const sendSignal = useCallback(() => {
        if (signal.type == null || signal.sending || activityId == null || questionId == null) return
        const { type, comment } = signal
        dispatchSignal({ type: "sendStarted" })
        measured("activity.quiz_signal", () =>
            call(activitiesService.signalQuizQuestion, {
                routeParams: { activityId },
                data: { quiz_question_id: questionId, signal_type: type, context: SIGNAL_CONTEXT, comment },
            })
        )
            .then(() => {
                amplitude.track("quiz.question.signal.submitted", {
                    signal_type: type,
                    context: SIGNAL_CONTEXT,
                    question_id: questionId,
                    comment,
                })
                dispatchSignal({ type: "sent" })
                notifyRef.current(QUIZ_REVIEW_TEXTS.signalSent, "success")
            })
            .catch(() => dispatchSignal({ type: "sendFailed" }))
    }, [activityId, call, measured, questionId, signal])

    // Une autre activité : le signalement ouvert se ferme.
    useEffect(() => {
        dispatchSignal({ type: "closed" })
    }, [activityId])

    return {
        // Pas de quiz tenté : pas d'avis à donner (la vue n'en montre pas).
        feedback: raw?.prerequisite_quiz != null ? { value: quizFeedbackOf(value), onChange: onFeedback } : null,
        onSignalQuestion,
        signal: signal.open
            ? {
                  visible: true,
                  type: signal.type,
                  comment: signal.comment,
                  sending: signal.sending,
                  failed: signal.failed,
                  onType: (type) => dispatchSignal({ type: "typeChosen", signal: type as QuizSignalType }),
                  onComment: (comment) => dispatchSignal({ type: "commentChanged", comment }),
                  onSend: sendSignal,
                  onClose: () => dispatchSignal({ type: "closed" }),
              }
            : null,
    }
}
